/**
 * receiptExtraInfo - resolver for profil-konfigurert tilleggsinfo i varemottak.
 *
 * Operatørprofilen kan peke ut 2-3 felt-navn (via UnifiedSettings-nøklene
 * receipt.extraInfoField1/2/3). Verdien er navnet på et felt fra
 * innkjøpsordrelinjen (POrderLineDto / POrderLineWithHeaderDto). Har feltet
 * verdi, vises verdien som tilleggsinfo til operatøren - kun verdien, ingen label.
 *
 * Samme prinsipp som plukkens getOrderLineFieldValue (usePickingData.ts), men
 * kildeagnostisk: resolveren leser camelCase-egenskapen direkte fra kilde-objektet,
 * slik at den fungerer både for POrderLineDto (kun linjefelt) og
 * POrderLineWithHeaderDto (linje + header-felt).
 */

/**
 * Vanlige felt-navn som er nyttige å konfigurere (kun forslag / hjelpetekst).
 * Resolveren er IKKE begrenset til denne lista - den matcher dynamisk mot
 * hvilken som helst egenskap på kilde-DTO-en (se resolveReceiptExtraInfo).
 *
 * Merk: header-felt (supplier*, *Ref, carrierName) finnes kun på
 * POrderLineWithHeaderDto (Åpent mottak). I ordrebasert mottak (POrderLineDto)
 * resolver de til undefined og vises ikke. Linjefelt fungerer overalt.
 */
export const RECEIPT_EXTRA_INFO_FIELDS: string[] = [
  'OrderLineFreeField1',
  'OrderLineFreeField2',
  'OrderLineFreeField3',
  'Description1',
  'ItemBarCode',
  'ItemGroup1',
  'ItemGroup2',
  'ItemGroup3',
  'ItemBatchNo',
  'StdLocationName',
  'DeliveryDate',
  'InboundShipmentNo',
  'OrderNoERP',
  // Kun Åpent mottak:
  'SupplierName',
  'SupplierOrderNo',
  'YourRef',
  'OurRef',
  'CarrierName',
];

/**
 * Resolver ett konfigurert felt-navn til en visbar verdi fra kilde-linjen.
 *
 * Dynamisk og case-insensitivt: feltnavnet matches mot egenskapsnavnene på
 * kilde-objektet (DTO-en). Dermed virker hvilket som helst felt som finnes på
 * POrderLineDto / POrderLineWithHeaderDto automatisk.
 *
 * Graceful: ukjent/feilskrevet feltnavn -> ingen treff -> undefined (vises ikke).
 * Returnerer også undefined ved NULL, tom streng (etter trim) eller ikke-primitive
 * verdier (objekter o.l.).
 */
export function resolveReceiptExtraInfo(
  source: unknown,
  fieldName?: string | null
): string | undefined {
  if (!source || typeof source !== 'object' || !fieldName) return undefined;

  const target = fieldName.toLowerCase().trim();
  if (!target) return undefined;

  const rec = source as Record<string, unknown>;

  // Finn egenskapen som matcher feltnavnet case-insensitivt.
  const matchedKey = Object.keys(rec).find((k) => k.toLowerCase() === target);
  if (!matchedKey) return undefined;

  const raw = rec[matchedKey];
  if (raw == null) return undefined;

  // Formatér kun primitive verdier / datoer. Objekter o.l. vises ikke.
  let str: string;
  if (raw instanceof Date) {
    str = isNaN(raw.getTime()) ? '' : raw.toLocaleDateString('no-NO');
  } else if (
    typeof raw === 'string' ||
    typeof raw === 'number' ||
    typeof raw === 'boolean'
  ) {
    str = String(raw);
  } else {
    return undefined;
  }

  str = str.trim();
  return str === '' ? undefined : str;
}

/**
 * Resolver en liste konfigurerte felt-navn til visbare verdier (kun de med innhold).
 * Brukes av visningskomponenten ReceiptExtraInfo.
 */
export function resolveReceiptExtraInfoValues(
  source: unknown,
  fieldNames: string[]
): string[] {
  if (!source || fieldNames.length === 0) return [];
  return fieldNames
    .map((name) => resolveReceiptExtraInfo(source, name))
    .filter((v): v is string => !!v);
}
